import { useEffect, useState, useContext } from "react";

import { Link, Typography } from "@mui/material";
import { getDashboard } from "@rubi/api-generated";
import { useParams, useNavigate } from "react-router";

import { getServiceConfig } from "@/common/serviceConfigurations";
import { mailTo } from "@/common/utils";
import { DisabledComponentDialog } from "@/components/DisabledComponent";
import { parseUserPermissions } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";
import { useMetabaseEmbedUrl } from "@/hooks/useMetabaseEmbedUrl";
import { IUserPermission } from "@/types/typesAlerts";

function ReportV2Analytics({ cisoDisplay }: { cisoDisplay: boolean }) {
  const [iframeUrl, setIframeUrl] = useState("");
  const themedIframeUrl = useMetabaseEmbedUrl(iframeUrl);
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthStateContext);
  const [reportPermissions] = useState(
    currentUser &&
      parseUserPermissions(currentUser?.permissions as IUserPermission[]),
  );
  // Check intel permissions when in intel mode (cisoDisplay), otherwise check report_v2 permissions
  const canViewDashboardRV2 = reportPermissions
    ? cisoDisplay
      ? reportPermissions.canViewDashboardIntel
      : reportPermissions.canViewDashboardRV2
    : true;

  const { isDevEnv } = useDevEnvironment();
  const currentOrgRegion = getServiceConfig(orgId ?? "", isDevEnv).apiRegion;

  useEffect(() => {
    getDashboard({
      query: {
        dashboard: currentOrgRegion === "us" ? 22 : 2,
        org_id: orgId ?? "",
      },
    }).then((res) => {
      const url = (res.data as { metabase_url: string } | undefined)
        ?.metabase_url;
      if (typeof url === "string") setIframeUrl(url);
    });
  }, [orgId, currentOrgRegion]);

  if (!canViewDashboardRV2) {
    return (
      <DisabledComponentDialog
        isDialogOpen={true}
        handleClose={() => {
          navigate(`/`);
        }}
        title="You do not have access to view analytics dashboard."
        message={
          <Typography>
            Please contact{" "}
            <Link
              rel="noopener noreferrer"
              target="_blank"
              href={mailTo({
                email: "support@tryrubicon.com",
                subject: "Requesting Access to View Analytics",
                body: `Hello Rubicon Support Team, 

                I would like to request access to view the analytics dashboard.

                Thank you. 

                Sincerely,

                ${currentUser?.first_name} ${currentUser?.last_name} 
                ${currentUser?.email}`,
              })}
              color="secondary"
            >
              support@tryrubicon.com
            </Link>{" "}
            to request access.
          </Typography>
        }
      />
    );
  }

  return (
    <div style={{ display: "flex", width: "100%", height: "100%" }}>
      <iframe
        src={themedIframeUrl}
        style={{
          flexGrow: 1,
          // Drop the browser's default inset frame around the embed.
          border: "none",
        }}
      ></iframe>
    </div>
  );
}

export default ReportV2Analytics;
